import { useState } from "react";
import { useAuthContext } from "@mavenmm/teamwork-auth";
import { useCurrentUser } from "./hooks/useCurrentUser";
import { AssistantSurface } from "./components/assistant/AssistantSurface";
import { useDefaultModel } from "./hooks/useDefaultModel";
import { useAvailableModels } from "./hooks/useAvailableModels";
import Footer from "./components/Footer";
import BulletinBoard from "./components/BulletinBoard";
import AppsGrid from "./components/AppsGrid";
import ExternalLinksSection from "./components/ExternalLinksSection";
import Carousel from "./components/Carousel";

const coreValues = [
  {
    icon: "/icons/core-1.png",
    heading: "We Before Me",
    body: `We win — together. We hold ourselves and each other accountable, ensuring no one is left behind. No egos, no silos — just a team doing whatever it takes to support one another. <strong>Titles don’t matter, but trust does</strong>. We believe in open communication, even when it’s hard. We always assume positive intent and seek to listen with empathy and understanding. If something needs doing, we step up and pitch in to make each other’s lives easier because when we lift each other up, we all succeed.`,
  },
  {
    icon: "/icons/core-2.png",
    heading: "Drivers,<br/>Not Passengers",
    body: `At Maven, things move fast—and we like it that way. Success here isn’t about waiting for direction; it’s about stepping up, taking ownership, and keeping things in motion. We don’t let projects stall, and we don’t play the blame game. Instead, we tackle challenges head-on, communicate what’s next, and make sure every piece moves forward. If you see a roadblock, you clear it. If something needs to happen, you make it happen. That’s the difference between working on a project and truly <strong>owning</strong> it.`,
  },
  {
    icon: "/icons/core-3.png",
    heading: "Your Potential,<br/>Our Priority",
    body: "We grow our own talent. We believe in investing in people who invest in themselves. We bet on people who are hungry to level up, not just clock in. We hire smart, talented, ambitious, courageous people and encourage them to try, learn from their mistakes, and get better every day. We don’t just build careers—<strong>we build leaders</strong>. Your growth fuels our future. ",
  },
  {
    icon: "/icons/core-4.png",
    heading: "Rethink, Reinvent,<br/>Refine, Repeat",
    body: `Innovation isn’t an option; it’s a necessity. We use tech, automation, and teamwork to get more done. We minimize unnecessary layers and work to reduce bottlenecks by building sustainable systems and staying focused on long-term impact. We evolve, adapt, and <strong>challenge the status quo</strong> of how an agency should work every step of the way. We’re in competition with ourselves, continuously trying to be better than we were yesterday.`,
  },
];


export default function Home() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { user, isAuthenticated } = useAuthContext();

  // Fetch current user from GraphQL (respects admin override, cached via React Query)
  const { user: currentUser } = useCurrentUser();

  // Chat state, filters and the ethical-wall query all live in AssistantSurface now,
  // shared with the /assistant page. Home only decides whether the modal is showing.

  // Shown on the trigger so which model will answer is visible before opening it.
  const { defaultModel } = useDefaultModel();
  const { models } = useAvailableModels();
  const assistantModelLabel = models.find((m) => m.id === defaultModel)?.label;

  // Get display name - use GraphQL data if available (respects override), fallback to auth context
  const displayName = currentUser?.first_name
    || (isAuthenticated && user?.firstName ? user.firstName : null);

  return (
    <main className="flex-1 flex flex-col">
      {/* Hero Section */}
      <section
        className="relative text-white px-6 pt-24 pb-12 bg-cover bg-bottom bg-no-repeat"
        style={{ backgroundImage: "url('/hero-bg.png')" }}
      >
        {/* AI Assistant Trigger - Left side */}
        <button
          onClick={() => setIsModalOpen(true)}
          className="fixed left-0 top-1/2 -translate-y-1/2 z-40 bg-maven-teal-500 hover:bg-maven-teal-700 text-white px-3 py-4 rounded-r-lg flex flex-col items-center gap-2 transition-colors"
          aria-label={`Open AI Assistant${assistantModelLabel ? ` (${assistantModelLabel})` : ""}`}
        >
          <img
            src="/icons/apps/icon-ai-assistant-robot.svg"
            alt="AI Assistant"
            style={{ width: "60px", height: "auto" }}
          />
          {/* Which model is about to answer. Worth stating up front: the assistant can
              run on several, and the answer you get depends on which. */}
          {assistantModelLabel && (
            <span className="max-w-[72px] text-center text-[10px] leading-tight text-white/90">
              {assistantModelLabel}
            </span>
          )}
        </button>

        {/* Core Values Carousel - full width, content constrained inside */}
        <div className="mb-10">
          <Carousel slides={coreValues} />
        </div>

        <div className="max-w-6xl mx-auto">
          <h1 className="font-bold text-center mb-8 text-5xl">
            {displayName ? `Welcome ${displayName}!` : "Welcome!"}
          </h1>
        </div>

        {/* Bulletin Board */}
        <div className="px-16">
          <BulletinBoard
            userId={currentUser?.email_address || user?.email || "anonymous"}
            userName={displayName || "Someone"}
          />
        </div>
      </section>

      {/* Internal Apps Section */}
      {/* Unchanged in light; Maven Navy in dark, matching Other Links below. Chris
          asked for the white background to go navy "to toggle on", so nobody sees a
          different homepage unless they opt in. Was an inline #fafafa, which is why
          no dark: utility could reach it. */}
      <section className="px-6 py-12 bg-[#fafafa] dark:bg-maven-gray-800">
        <div className="max-w-6xl mx-auto">
          <AppsGrid userId={currentUser?.email_address || user?.email || "anonymous"} />
        </div>
      </section>

      {/* External Links Section */}
      <ExternalLinksSection userEmail={currentUser?.email_address || user?.email || "anonymous"} />

      {/* Footer */}
      <Footer />

      {/* Modal overlay - always mounted to preserve chat state */}
      <AssistantModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </main>
  );
}


/**
 * Dialog shell for the assistant. The chat itself lives in AssistantSurface, shared
 * with the /assistant page — this only supplies the overlay, the panel box, and the
 * close affordance that a modal has and a page does not.
 *
 * Kept mounted while closed so chat state survives open/close.
 */
function AssistantModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center ${isOpen ? "" : "hidden"}`}>
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-4xl h-[700px] mx-4 bg-white rounded-lg shadow-xl flex flex-col overflow-hidden">
        <AssistantSurface onClose={onClose} enabled={isOpen} />
      </div>
    </div>
  );
}
